import type { ComponentPropsWithRef, CSSProperties, JSX } from "react";
import { useId } from "react";
import { cx } from "../cx";

export interface InputProps extends Omit<ComponentPropsWithRef<"input">, "className" | "style"> {
  /** Caps 12px label rendered above the control. */
  label?: string | undefined;
  /** Muted helper line below the control. */
  hint?: string | undefined;
  /** Error message; replaces the hint, sets `aria-invalid`, turns the border red. */
  error?: string | undefined;
  /** Invalid state without a message (also sets `aria-invalid`). */
  invalid?: boolean | undefined;
  /** Render the value in IBM Plex Mono (URLs, tokens, dh). */
  mono?: boolean | undefined;
  /** Class for the outer field wrapper. */
  className?: string | undefined;
  /** Style for the outer field wrapper. */
  style?: CSSProperties | undefined;
  /** Class for the input element itself. */
  inputClassName?: string | undefined;
  /** Style for the input element itself. */
  inputStyle?: CSSProperties | undefined;
}

/**
 * Text input with caps label, hint/error line, and volt focus ring. Ported
 * from design/tarski-kit/components/forms/Input.jsx.
 */
export function Input({
  label,
  hint,
  error,
  invalid = false,
  mono = false,
  className,
  style,
  inputClassName,
  inputStyle,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedBy,
  ...rest
}: InputProps): JSX.Element {
  const noteId = useId();
  const message = error ?? hint;
  const isInvalid = invalid || error !== undefined;
  const describedBy = [ariaDescribedBy, message === undefined ? undefined : noteId]
    .filter((part): part is string => typeof part === "string" && part.length > 0)
    .join(" ");
  return (
    <span className={cx("tk-field", "tk-input", className)} style={style}>
      <label className="tk-field__control">
        {label === undefined ? null : <span className="tk-field__label">{label}</span>}
        <input
          {...rest}
          className={cx(
            "tk-input__control",
            mono && "tk-input__control--mono",
            isInvalid && "tk-input__control--invalid",
            inputClassName,
          )}
          style={inputStyle}
          aria-invalid={isInvalid ? true : ariaInvalid}
          aria-describedby={describedBy.length > 0 ? describedBy : undefined}
        />
      </label>
      {message === undefined ? null : (
        <span
          id={noteId}
          className={cx("tk-field__note", error !== undefined && "tk-field__note--error")}
        >
          {message}
        </span>
      )}
    </span>
  );
}
